import React, { useEffect, useState, useCallback } from 'react';
import { adminApi } from '../utils/api';
import { Card, StatCard, Table, TR, TD, Badge, PageHeader, Pagination, fmtDate, Modal } from '../components/UI';
import RocketLoader from '../components/RocketLoader';

const MODE_COLORS    = { basic: 'blue', personalized: 'purple' };
const SEN_COLORS     = { junior: 'green', mid: 'blue', senior: 'red' };
const MODE_LABELS    = { basic: 'Basic', personalized: 'Personalized' };
const SEN_LABELS     = { junior: 'Junior', mid: 'Mid', senior: 'Senior' };

export const InterviewsPage = () => {
  const [stats,    setStats]    = useState(null);
  const [rows,     setRows]     = useState([]);
  const [total,    setTotal]    = useState(0);
  const [pages,    setPages]    = useState(1);
  const [page,     setPage]     = useState(1);
  const [loading,  setLoading]  = useState(true);
  const [search,   setSearch]   = useState('');
  const [mode,     setMode]     = useState('');
  const [seniority,setSeniority]= useState('');
  const [deleting, setDeleting] = useState(null);
  const [detail,   setDetail]   = useState(null);

  useEffect(() => {
    adminApi.getInterviewStats().then(setStats).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await adminApi.getInterviews({ page, limit: 20, search: search.trim(), mode, seniority });
      setRows(data.interviews);
      setTotal(data.total);
      setPages(data.pages);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [page, search, mode, seniority]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [search, mode, seniority]);

  const openDetail = async (id) => {
    try {
      const iv = await adminApi.getInterview(id);
      setDetail(iv);
    } catch (e) { alert(e.message); }
  };

  const handleDelete = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm('Delete this interview session? This cannot be undone.')) return;
    setDeleting(id);
    try {
      await adminApi.deleteInterview(id);
      setRows(prev => prev.filter(r => r._id !== id));
      setTotal(t => t - 1);
      if (detail?._id === id) setDetail(null);
    } catch (e) { alert(e.message); }
    finally { setDeleting(null); }
  };

  return (
    <div>
      <PageHeader
        title={`Interview Sessions (${total})`}
        subtitle="View and manage all AI-generated interview prep sessions"
      />

      {/* Stats row */}
      {stats && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(160px,1fr))', gap: 14, marginBottom: 24 }}>
          <StatCard icon="🎤" label="Total Sessions"   value={stats.total}                 color="#7E22CE" />
          <StatCard icon="⚡" label="Basic Mode"       value={stats.byMode.basic}           color="#2563EB" />
          <StatCard icon="🎯" label="Personalized"     value={stats.byMode.personalized}    color="#9333EA" />
          <StatCard icon="🌱" label="Junior"           value={stats.bySeniority.junior}     color="#15803D" />
          <StatCard icon="🚀" label="Mid-level"        value={stats.bySeniority.mid}        color="#0891B2" />
          <StatCard icon="⭐" label="Senior"           value={stats.bySeniority.senior}     color="#EF4444" />
        </div>
      )}

      {/* Top users */}
      {stats?.topUsers?.length > 0 && (
        <Card style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#0F172A', marginBottom: 12 }}>Top Users by Sessions</div>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            {stats.topUsers.map((u, i) => (
              <div key={i} style={{ background: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: 8, padding: '8px 14px', fontSize: 12 }}>
                <span style={{ fontWeight: 700, color: '#0F172A' }}>{u.name || u.email || 'Unknown'}</span>
                {u.email && u.name && <span style={{ color: '#64748B', marginLeft: 6 }}>{u.email}</span>}
                <span style={{ marginLeft: 8, background: '#7E22CE22', color: '#7E22CE', borderRadius: 20, padding: '1px 8px', fontWeight: 700 }}>{u.count} sessions</span>
                {u.plan === 'pro' && <span style={{ marginLeft: 6, background: '#F3E8FF', color: '#7E22CE', border: '1px solid #E9D5FF', borderRadius: 20, padding: '1px 7px', fontSize: 10, fontWeight: 700 }}>Pro</span>}
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Filters */}
      <Card style={{ marginBottom: 16, padding: '14px 16px' }}>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
          <input
            value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search by user name or email…"
            style={{ flex: 1, minWidth: 200, background: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: 7, padding: '8px 12px', color: '#0F172A', fontSize: 13, outline: 'none' }}
          />
          <span style={{ color: '#64748B', fontSize: 13, flexShrink: 0 }}>Mode:</span>
          {[['', 'All'], ['basic', 'Basic'], ['personalized', 'Personalized']].map(([v, l]) => (
            <button key={v} onClick={() => setMode(v)}
              style={{ background: mode === v ? '#EF1D28' : '#F8FAFC', color: mode === v ? 'white' : '#64748B', border: `1px solid ${mode === v ? '#EF1D28' : '#E2E8F0'}`, borderRadius: 20, padding: '4px 14px', fontSize: 12, fontWeight: 600, cursor: 'pointer', flexShrink: 0 }}>
              {l}
            </button>
          ))}
          <span style={{ color: '#64748B', fontSize: 13, flexShrink: 0 }}>Level:</span>
          {[['', 'All'], ['junior', 'Junior'], ['mid', 'Mid'], ['senior', 'Senior']].map(([v, l]) => (
            <button key={v} onClick={() => setSeniority(v)}
              style={{ background: seniority === v ? '#EF1D28' : '#F8FAFC', color: seniority === v ? 'white' : '#64748B', border: `1px solid ${seniority === v ? '#EF1D28' : '#E2E8F0'}`, borderRadius: 20, padding: '4px 14px', fontSize: 12, fontWeight: 600, cursor: 'pointer', flexShrink: 0 }}>
              {l}
            </button>
          ))}
        </div>
      </Card>

      {/* Table */}
      <Card style={{ padding: 0 }}>
        <Table headers={['Session', 'User', 'Plan', 'Mode', 'Level', 'Questions', 'Practiced', 'Created', 'Action']}>
          {loading ? (
            <TR><TD><div style={{ padding: 30, display: 'flex', justifyContent: 'center' }}><RocketLoader size={120} message="Loading sessions…" /></div></TD></TR>
          ) : rows.length === 0 ? (
            <TR><TD colSpan={9}><div style={{ padding: 30, color: '#64748B', textAlign: 'center' }}>No interview sessions found</div></TD></TR>
          ) : rows.map(iv => (
            <TR key={iv._id} onClick={() => openDetail(iv._id)}>
              <TD>
                <div style={{ fontWeight: 600, color: '#0F172A', fontSize: 13 }}>{iv.title}</div>
                <div style={{ color: '#64748B', fontSize: 11 }}>{iv._id}</div>
              </TD>
              <TD>
                {iv.userId ? (
                  <div>
                    <div style={{ color: '#334155', fontSize: 13 }}>{iv.userId.name}</div>
                    <div style={{ color: '#64748B', fontSize: 11 }}>{iv.userId.email}</div>
                  </div>
                ) : <span style={{ color: '#CBD5E1', fontSize: 12 }}>Deleted user</span>}
              </TD>
              <TD>
                {iv.userId && <Badge color={iv.userId.plan === 'pro' ? 'purple' : 'gray'}>{iv.userId.plan === 'pro' ? '⭐ Pro' : 'Free'}</Badge>}
              </TD>
              <TD><Badge color={MODE_COLORS[iv.mode] || 'gray'}>{MODE_LABELS[iv.mode] || iv.mode}</Badge></TD>
              <TD><Badge color={SEN_COLORS[iv.seniority] || 'gray'}>{SEN_LABELS[iv.seniority] || iv.seniority}</Badge></TD>
              <TD style={{ color: '#334155', fontSize: 13, textAlign: 'center' }}>{iv.questionCount}</TD>
              <TD style={{ color: '#334155', fontSize: 13, textAlign: 'center' }}>
                <span style={{ color: iv.practicedCount > 0 ? '#15803D' : '#CBD5E1', fontWeight: iv.practicedCount > 0 ? 700 : 400 }}>
                  {iv.practicedCount}
                </span>
              </TD>
              <TD style={{ color: '#64748B', fontSize: 12 }}>{fmtDate(iv.createdAt)}</TD>
              <TD>
                <button onClick={e => handleDelete(iv._id, e)} disabled={deleting === iv._id}
                  style={{ background: '#FEF2F2', color: '#DC2626', border: '1px solid #FECACA', borderRadius: 7, padding: '5px 10px', fontSize: 11, fontWeight: 700, cursor: 'pointer', opacity: deleting === iv._id ? 0.5 : 1 }}>
                  {deleting === iv._id ? '…' : 'Delete'}
                </button>
              </TD>
            </TR>
          ))}
        </Table>
      </Card>

      <Pagination page={page} pages={pages} onChange={setPage} />

      {/* Detail modal */}
      {detail && (
        <Modal title={detail.title} onClose={() => setDetail(null)} width={620}>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
            <Badge color={MODE_COLORS[detail.mode] || 'gray'}>{MODE_LABELS[detail.mode] || detail.mode}</Badge>
            <Badge color={SEN_COLORS[detail.seniority] || 'gray'}>{SEN_LABELS[detail.seniority] || detail.seniority}</Badge>
            {detail.userId && <Badge color={detail.userId.plan === 'pro' ? 'purple' : 'gray'}>{detail.userId.plan === 'pro' ? '⭐ Pro' : 'Free'}</Badge>}
          </div>
          {detail.userId && (
            <div style={{ background: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: 13 }}>
              <span style={{ fontWeight: 700, color: '#0F172A' }}>{detail.userId.name}</span>
              <span style={{ color: '#64748B', marginLeft: 8 }}>{detail.userId.email}</span>
            </div>
          )}
          <div style={{ fontSize: 12, fontWeight: 700, color: '#64748B', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 10 }}>
            Questions ({detail.questions?.length || 0})
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, maxHeight: 420, overflowY: 'auto' }}>
            {(detail.questions || []).map((q, i) => (
              <div key={i} style={{ background: q.practice?.practicedAt ? '#F0FDF4' : '#F8FAFC', border: `1px solid ${q.practice?.practicedAt ? '#BBF7D0' : '#E2E8F0'}`, borderRadius: 8, padding: '10px 14px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#0F172A', flex: 1 }}>{i + 1}. {q.question}</div>
                  <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                    <Badge color={q.type === 'followup' ? 'yellow' : 'blue'}>{q.type === 'followup' ? 'Follow-up' : 'Main'}</Badge>
                    {q.practice?.practicedAt && (
                      <Badge color="green">Score: {q.practice.score ?? '—'}/10</Badge>
                    )}
                  </div>
                </div>
                {q.practice?.practicedAt && q.practice.userAnswer && (
                  <div style={{ marginTop: 8, fontSize: 12, color: '#374151', background: 'white', border: '1px solid #E2E8F0', borderRadius: 6, padding: '8px 10px' }}>
                    <span style={{ fontWeight: 700, color: '#64748B' }}>User's answer: </span>
                    {q.practice.userAnswer}
                  </div>
                )}
              </div>
            ))}
            {(!detail.questions || detail.questions.length === 0) && (
              <div style={{ color: '#64748B', fontSize: 13, textAlign: 'center', padding: 20 }}>No questions generated yet</div>
            )}
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 16, paddingTop: 16, borderTop: '1px solid #E2E8F0' }}>
            <span style={{ fontSize: 12, color: '#64748B' }}>Created {fmtDate(detail.createdAt)}</span>
            <button
              onClick={e => handleDelete(detail._id, e)}
              disabled={deleting === detail._id}
              style={{ background: '#FEF2F2', color: '#DC2626', border: '1px solid #FECACA', borderRadius: 7, padding: '7px 14px', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
              {deleting === detail._id ? 'Deleting…' : 'Delete Session'}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
};
